import React, { useState, useEffect } from 'react'
import '../navigation.css'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/core/auth/AuthContext'
import { useAppStore } from '@/app/store'
import { TabConfig, NavigationItem, isNavigationGroup } from '../types'
import { useNavigationState } from '../hooks/useNavigationState'
import { NavigationGroup } from './NavigationGroup'
import { LanguageSwitcher, Language } from '@/ui/components/atoms/LanguageSwitcher'
import { NotificationBell } from '@/features/admin/tabs/boards/NotificationBell'
import { useNotifications } from '@/features/admin/tabs/boards/api'
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  FolderIcon,
  BriefcaseIcon,
  BanknotesIcon,
  UserGroupIcon,
  UserPlusIcon,
  DocumentDuplicateIcon,
  PresentationChartLineIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  WrenchScrewdriverIcon,
  DocumentCurrencyEuroIcon,
  ChatBubbleLeftRightIcon,
  PlusCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'

interface NavigationRailProps {
  tabs: TabConfig[]
  navigationItems?: NavigationItem[]  // New: grouped navigation structure
  className?: string
  onCollapse?: (collapsed: boolean) => void
  isCollapsed?: boolean
  mobileOpen?: boolean
  onMobileToggle?: (open: boolean) => void
  getBadgeValue?: (tabId: string) => string | number | undefined
}

const iconMap: Record<string, React.ComponentType<any>> = {
  // Dashboard icons
  DashboardIcon: ComputerDesktopIcon,
  HomeIcon: HomeIcon,
  ComputerDesktopIcon: ComputerDesktopIcon,

  // User/Customer icons
  UsersIcon: UsersIcon,
  UserGroupIcon: UserGroupIcon,
  CustomersIcon: UsersIcon,

  // Pipeline icons
  PipelineIcon: Squares2X2Icon,
  BriefcaseIcon: BriefcaseIcon,
  Squares2X2Icon: Squares2X2Icon,

  // Task icons
  TaskIcon: ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon: ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon: ClipboardDocumentCheckIcon,
  CheckCircleIcon: CheckCircleIcon,

  // Admin icons
  AdminIcon: UserPlusIcon,
  ShieldCheckIcon: ShieldCheckIcon,
  UserPlusIcon: UserPlusIcon,

  // Document icons
  DocumentIcon: DocumentTextIcon,
  DocumentTextIcon: DocumentTextIcon,
  DocumentDuplicateIcon: DocumentDuplicateIcon,

  // Chart/Report icons
  ChartIcon: ChartBarIcon,
  ChartBarIcon: ChartBarIcon,
  PresentationChartLineIcon: PresentationChartLineIcon,

  // Settings icons
  SettingsIcon: CogIcon,
  CogIcon: CogIcon,
  Cog6ToothIcon: Cog6ToothIcon,

  // AI/IA icons
  SparklesIcon: SparklesIcon,

  // Communication icons
  EnvelopeIcon: EnvelopeIcon,
  ChatBubbleLeftRightIcon: ChatBubbleLeftRightIcon,
  PhoneIcon: PhoneIcon,

  // Other icons
  CalendarIcon: CalendarIcon,
  CalendarDaysIcon: CalendarDaysIcon,
  FolderIcon: FolderIcon,
  BanknotesIcon: BanknotesIcon,
  ClockIcon: ClockIcon,
  BuildingOfficeIcon: BuildingOfficeIcon,
  WrenchScrewdriverIcon: WrenchScrewdriverIcon,
  DocumentCurrencyEuroIcon: DocumentCurrencyEuroIcon,
  PlusCircleIcon: PlusCircleIcon,
}

export const NavigationRail: React.FC<NavigationRailProps> = ({
  tabs,
  navigationItems,
  className = '',
  onCollapse,
  isCollapsed: controlledCollapsed,
  mobileOpen = false,
  onMobileToggle,
  getBadgeValue
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAdmin, isExpert, isClient, isBanque, isBureau } = useAuth()
  const { language, setLanguage } = useAppStore()
  // Board notifications count → badge on the "boards" nav item (admin/bureau only).
  const { data: boardsNotif } = useNotifications(isAdmin || isBureau)
  const boardsUnread = boardsNotif?.unread || 0
  const resolveBadge = (id: string) =>
    (id === 'boards' && boardsUnread > 0) ? boardsUnread : getBadgeValue?.(id)
  const [localCollapsed, setLocalCollapsed] = useState(false)

  // Use controlled state if provided, otherwise use local state
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : localCollapsed
  const [isMobile, setIsMobile] = useState(false)

  // Navigation state for collapsible groups
  const itemsToUse = navigationItems || tabs
  const { toggleGroup, isGroupExpanded } = useNavigationState(itemsToUse)

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close mobile menu when clicking a navigation item
  const handleMobileNavigation = (path: string) => {
    handleNavigation(path)
    if (isMobile && onMobileToggle) {
      onMobileToggle(false)
    }
  }

  // Get module class for role-specific styling
  const getModuleClass = () => {
    if (isAdmin) return 'module-admin'
    if (isExpert) return 'module-expert'
    if (isBureau) return 'module-bureau'
    if (isClient) return 'module-client'
    if (isBanque) return 'module-banque'
    return ''
  }

  // Apply module class to body
  useEffect(() => {
    const moduleClass = getModuleClass()
    if (moduleClass) {
      document.body.classList.add(moduleClass)
      return () => {
        document.body.classList.remove(moduleClass)
      }
    }
  }, [user?.role])

  const getRoleLabel = () => {
    if (isAdmin) return 'Admin'
    if (isExpert) return 'Expert'
    if (isBureau) return 'Bureau'
    if (isClient) return 'Client'
    if (isBanque) return 'Banque'
    return ''
  }

  const handleLogout = async () => {
    await logout()
    navigate('/signin')
  }

  const handleNavigation = (path: string) => {
    // If clicking on the already active tab, pass resetView state to allow
    // the tab to reset to its initial state (e.g., Facturation → Recherche)
    if (isActiveTab(path)) {
      navigate(path, { state: { resetView: true } })
    } else {
      navigate(path)
    }
  }

  const isActiveTab = (path: string) => {
    return location.pathname.startsWith(path)
  }

  const renderIcon = (icon?: string | React.ReactNode) => {
    if (!icon) return null
    if (typeof icon === 'string' && iconMap[icon]) {
      const IconComponent = iconMap[icon]
      return <IconComponent className="w-5 h-5" />
    }
    return icon
  }

  // Available languages
  const availableLanguages: Language[] = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  ]

  // Check if current user role should show language switcher
  const showLanguageSwitcher = isClient || isBanque

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div 
          className={`mobile-nav-backdrop ${mobileOpen ? 'show' : ''}`}
          onClick={() => onMobileToggle?.(false)}
        />
      )}
      
      <nav className={`shared-navigation-rail ${isCollapsed && !isMobile ? 'collapsed' : ''} ${isMobile && mobileOpen ? 'mobile-open' : ''} ${className}`}>
      {/* Rail Header */}
      <div className="rail-header">
        <div
          className="logo-container"
          onClick={() => {
            const basePath = location.pathname.startsWith('/bureau') ? '/bureau' :
                           location.pathname.startsWith('/admin') ? '/admin' :
                           location.pathname.startsWith('/expert') ? '/expert' :
                           `/${user?.role}`
            const path = `${basePath}/whats-new`
            isMobile ? handleMobileNavigation(path) : handleNavigation(path)
          }}
          style={{ cursor: 'pointer' }}
          title="Accueil"
        >
          {!isCollapsed && (
            <img src="/images/logo-white.svg" alt="Logo" className="logo-image" />
          )}
        </div>
        {!isMobile && (
          <button
            onClick={() => {
              const newState = !isCollapsed
              if (controlledCollapsed === undefined) {
                setLocalCollapsed(newState)
              }
              onCollapse?.(newState)
            }}
            className="collapse-toggle"
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {isCollapsed ? (
              <Bars3Icon className="w-5 h-5" />
            ) : (
              <XMarkIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {isMobile && (
          <button
            onClick={() => onMobileToggle?.(false)}
            className="mobile-close"
            aria-label="Close navigation"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Rail Menu */}
      <div className="rail-menu">
        <ul className="menu-list">
          {itemsToUse.map((item) => {
            // Handle NavigationGroup
            if (isNavigationGroup(item)) {
              return (
                <NavigationGroup
                  key={item.id}
                  group={item}
                  isRailCollapsed={isCollapsed}
                  isMobile={isMobile}
                  isExpanded={isGroupExpanded(item.id)}
                  onToggle={() => toggleGroup(item.id)}
                  onNavigate={(path) =>
                    isMobile ? handleMobileNavigation(path) : handleNavigation(path)
                  }
                  activeTabPath={location.pathname}
                  renderIcon={renderIcon}
                  getBadgeValue={resolveBadge}
                  isAdmin={isAdmin}
                />
              )
            }

            // Handle regular TabConfig
            const tab = item as TabConfig
            if (tab.hidden) return null

            const hasPermission =
              !tab.permissions ||
              tab.permissions.every((permission) => {
                // Admin-only permission check
                if (permission === 'admin_only') return isAdmin
                return true
              })

            if (!hasPermission) return null

            // Get badge value from callback if provided, otherwise use tab.badge
            const badgeValue = resolveBadge(tab.id) ?? tab.badge?.value

            return (
              <React.Fragment key={tab.id}>
                {tab.divider && <li className="my-2 border-t border-white/10" />}
                <li className="menu-item">
                  <button
                    onClick={() =>
                      isMobile
                        ? handleMobileNavigation(tab.path)
                        : handleNavigation(tab.path)
                    }
                    className={`menu-button ${isActiveTab(tab.path) ? 'active' : ''}`}
                    title={isCollapsed ? tab.label : undefined}
                    data-nav-id={tab.id}
                  >
                    <span className="menu-icon">{renderIcon(tab.icon)}</span>
                    {(!isCollapsed || isMobile) && (
                      <>
                        <span className="menu-label">{tab.label}</span>
                        {badgeValue && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full animate-pulse">
                            {badgeValue}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              </React.Fragment>
            )
          })}
        </ul>

        <div className="version-badge">
          {isCollapsed && !isMobile ? getRoleLabel()[0] : getRoleLabel()}
        </div>
      </div>

      {/* Rail Footer */}
      <div className="rail-footer">
        {/* Language Switcher - only for client and banque roles */}
        {showLanguageSwitcher && (
          <div className="footer-language-section">
            <LanguageSwitcher
              currentLanguage={language}
              languages={availableLanguages}
              onLanguageChange={(lang) => setLanguage(lang as 'fr' | 'en' | 'nl' | 'de')}
              collapsed={isCollapsed && !isMobile}
            />
          </div>
        )}

        <div
          className="footer-user-section"
          onClick={() => navigate(`/${user?.role}/settings`)}
          style={{ cursor: 'pointer' }}
          title="Paramètres"
        >
          <div className="user-avatar">
            <span className="avatar-initials">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </span>
          </div>
          {(!isCollapsed || isMobile) && (
            <div className="user-info">
              <div className="user-name">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="user-email">
                {user?.email}
              </div>
            </div>
          )}
          {(isAdmin || isBureau) && (
            // Stop propagation so opening notifications doesn't also trigger
            // the section's navigate-to-settings click.
            <div className="footer-user-bell" onClick={(e) => e.stopPropagation()}>
              <NotificationBell />
            </div>
          )}
        </div>
        
        <button
          onClick={handleLogout}
          className="footer-logout-button"
          title="Logout"
        >
          <ArrowLeftOnRectangleIcon className="w-5 h-5" />
          {(!isCollapsed || isMobile) && (
            <span>Déconnexion</span>
          )}
        </button>
      </div>
      </nav>
    </>
  )
}